import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@skygroup/shared/API/apiClient';
import endpoints from '../../utils/endpoints';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  clearCustomerInfo,
  setWholeCart,
} from '../ShoppingCart/ShoppingCartSliceV2';
import openNotification, {
  NotificationType,
} from '../../utils/openNotification';
import { ApiStatus } from '@skygroup/shared/components/crud-helpers/ModelApiMessage';
import { OrderProductStatus } from '@skygroup/shared/constants/OrderProductConsts';
import { message } from 'antd';

export const useOrderGetApi = ({
  orderSecret,
  allProductsWithFreeCancellation = undefined,
  enabled = undefined,
}: {
  orderSecret: string;
  allProductsWithFreeCancellation?: boolean;
  enabled?: boolean;
}) => {
  const { checkout: t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const dispatch = useDispatch();

  const orderGet = useQuery<any, any>(
    ['order-get', orderSecret],
    async ({ signal }) => {
      return await apiClient
        .get(
          `${endpoints.ORDER.GET}?order_secret=${orderSecret}${
            allProductsWithFreeCancellation ? '&validate_order_status=1' : ''
          }`,
          { signal } // Attach the AbortSignal to cancel request if needed
        )
        .then((data) => {
          if (data.data.status == ApiStatus.error) {
            return {
              cartNotValid: true,
              isSaved: true,
            };
          }

          // if success, return the data
          return { ...data.data, isSaved: true };
        })
        .catch((err) => {
          if (err.response?.status === 500) {
            // if 500 error, continue to refetch
            console.error(
              `${err.response?.data?.message} ${err.response?.status}: continue to refetch for order get api`
            );
            return { continueRefetch: true };
          }

          dispatch(clearCustomerInfo());
          localStorage.removeItem('currentCustomer');

          // TODO change to specific code
          if (err.response.data.statusCode === 404) {
            return {
              cartNotValid: true,
              isSaved: true,
            };
          }

          // if not 404 error, show error notification and return cartNotValid true and isSaved true
          openNotification(t.orderFailedToLoad, NotificationType.Error);
          return { continueRefetch: true };
        });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled:
        enabled !== false &&
        !!orderSecret &&
        allProductsWithFreeCancellation !== false,
    }
  );

  return orderGet;
};

export const updateGuestInfo = async (
  formData: any,
  orderSecret: string,
  setIsLoading: any
) => {
  const body = {
    order_secret: orderSecret,
    customer_info: formData.customerInfo,
    ...formData,
  };
  if (body?.orderSecret) {
    delete body['orderSecret'];
  }

  setIsLoading(true);
  return apiClient
    .post(`${endpoints.ORDER.UPDATE_GUESTS}`, body)
    .then((res) => {
      return res;
    })
    .finally(() => {
      setIsLoading(false);
    });
};
export const updateCommission = (
  formData: any,
  orderSecret: string,
  setIsLoading: any
) => {
  setIsLoading(true);
  return apiClient
    .post(
      `${endpoints.AGENT.UPDATE_COMMISSION}?orderSecret=${orderSecret}`,
      formData
    )
    .then((res) => {
      if (res.status > 300 && res.status < 500) {
        message.error(
          res?.data?.data?.message || 'Failed to update commission'
        );
      }
      return res;
    })
    .catch((err) => {
      message.error(
        err?.response?.data?.message[0] || 'Failed to update commission'
      );
      return err;
    })
    .finally(() => {
      setIsLoading(false);
    });
};

export const updateOrderApi = (
  formData: any,
  orderSecret: string,
  setIsLoading: any
) => {
  formData['secret'] = orderSecret;

  setIsLoading(true);
  return apiClient
    .post(`${endpoints.ORDER.UPDATE}`, formData)
    .then((res) => {
      return res;
    })
    .finally(() => {
      setIsLoading(false);
    });
};

export const useCreateOrderApi = () => {
  const dispatch = useDispatch();
  const { ShoppingCartV2 } = useSelector(
    (state: RootState) => state.shoppingCartV2
  );

    const createOrderApi = async (
        formData: any,
        setIsLoading: any,
    ) => {
        // Attach stored click_id from sessionStorage to the create-order payload when available.
        let body = formData;
        try {
            const clickId = sessionStorage.getItem('click_id');
            if (clickId && !Object.prototype.hasOwnProperty.call(formData, 'click_id')) {
                body = {
                    ...body,
                    click_id: clickId,
                };
            }
        } catch (e) {
            // Fail silently if sessionStorage is unavailable
            console.error('Failed to read click_id from sessionStorage', e);
        }

        // Forward bot_session_id when the cart was hydrated from a travel-agent
        // bot conversation (set by useBotSessionHydration). The BE persists it
        // on tbl_orders.bot_session_id so the backoffice can link orders back
        // to the originating chat.
        const botSessionId = ShoppingCartV2?.bot_session_id;
        if (
            botSessionId &&
            !Object.prototype.hasOwnProperty.call(formData, 'bot_session_id')
        ) {
            body = {
                ...body,
                bot_session_id: botSessionId,
            };
        }

        setIsLoading(true);
        dispatch(setWholeCart({...ShoppingCartV2, isSaved:true}));
        return apiClient
            .post(endpoints.ORDER.CREATE_MS, body)
            .then((res) => { return res; })
            .finally(() => {
                setIsLoading(false);
            });
    };

  return createOrderApi;
};

export const cancelAutoCancellationAPi = async ({
  order_secret,
  order_product_id,
  setIsLoading,
}: {
  order_secret: string;
  order_product_id: number;
  setIsLoading: any;
}) => {
  setIsLoading(true);
  return apiClient
    .post(endpoints.ORDER.CANCEL_AUTO_CANCELLATION_ROOM, {
      order_secret,
      order_product_id,
    })
    .then((res) => {
      return res;
    })
    .finally(() => {
      setIsLoading(false);
    });
};

export const loadPayment = (
  formData: any,
  setIsLoading: any,
  currentLang: any
) => {
  setIsLoading(true);
  return apiClient
    .post(endpoints.PAYMENT.FORM_V2 + '?lang=' + currentLang, formData)
    .then((res) => {
      return res;
    })
    .finally(() => {
      setIsLoading(false);
    });
};

export const updateFormCustomData = (formData: any) => {
  return apiClient
    .post(endpoints.PAYMENT.CUSTOM_DATA_UPDATE, formData)
    .then((res) => {
      return res;
    })
    .finally(() => {});
};

export const loadTransaction = (
  formData: { transaction_id: string; secret: string },
  setIsLoading: any
) => {
  setIsLoading(true);
  return apiClient
    .get(
      endpoints.PAYMENT.TRANSACTION_STATUS +
        `?transaction_id=${formData.transaction_id}&secret=${formData.secret}`
    )
    .then((res) => {
      return res;
    })
    .finally(() => {
      setIsLoading(false);
    });
};
